// Tracker / analytics URL filter and form-shape heuristic for ft.js's
// fetch + XHR intercept (Method 2 in script/src/ft.js).
//
// Why this exists: WordPress sites in particular fire a lot of background
// XHR/fetch traffic — Google Analytics, GTM, Meta pixel, Hotjar, Sentry,
// admin-ajax pings, etc. Some of those payloads include strings like
// "user_name" or "page_title" or "email_hash" and tripped the previous
// `looksFormy` regex, producing phantom "leads" with no email and a
// form_label that read "google-analytics.com/g/collect".
//
// Two layers:
//   1. `looksLikeTracker(url)` — host + path deny-list. Cheapest filter.
//   2. `hasRealContactField(fields)` — require an email-shaped value or
//      a populated phone / first / last / full name field, not just the
//      presence of a key whose name contains "name". Stops GA4-style
//      `user_properties.name = "engaged"` from slipping through.
//
// Both functions are pure + side-effect free for easy unit testing.

const TRACKER_HOST_RE = /(^|\.)(google-analytics\.com|googletagmanager\.com|analytics\.google\.com|doubleclick\.net|googleadservices\.com|googlesyndication\.com|facebook\.com|connect\.facebook\.net|bat\.bing\.com|t\.co|linkedin\.com|licdn\.com|hotjar\.com|hotjar\.io|clarity\.ms|fullstory\.com|segment\.io|segment\.com|mixpanel\.com|amplitude\.com|posthog\.com|sentry\.io|ingest\.sentry\.io|datadoghq\.com|newrelic\.com|cloudflareinsights\.com|tiktok\.com|tiktokv\.com|snapchat\.com|sc-static\.net|pinterest\.com|reddit\.com|redditstatic\.com|cdn\.heapanalytics\.com|stats\.wp\.com|jetpack\.wordpress\.com)$/i;

const TRACKER_PATH_RE = /\/(g\/collect|collect\?|gtag\/|gtm\.js|analytics\.js|ga\.js|fbevents\.js|tr\b|insight\.min\.js|sentry|datadog)/i;

// WordPress-specific: admin-ajax.php is the kitchen-sink endpoint and
// most of its traffic isn't form submissions (Jetpack stats, plugin
// telemetry, etc.). Real form plugins go through it too, but they
// fire a native submit a moment before, and the lastNativeSubmitAt
// guard in ft.js (2 second window) suppresses the AJAX echo. So we
// don't deny-list admin-ajax outright — that would lose legitimate
// captures for sites whose plugins skip the native submit step.

export function looksLikeTracker(url) {
  if (!url) return false;
  const s = String(url);
  try {
    // Provide a base in case `url` is relative — we still want to test
    // the resolved hostname.
    const u = new URL(s, "https://_base_/");
    if (TRACKER_HOST_RE.test(u.hostname)) return true;
    if (TRACKER_PATH_RE.test(u.pathname + (u.search || ""))) return true;
  } catch (_e) {
    // URL constructor threw — fall back to substring path check.
    if (TRACKER_PATH_RE.test(s)) return true;
  }
  return false;
}

const CONTACT_KEY_RE =
  /email|phone|tel|mobile|first[\s_\-]?name|last[\s_\-]?name|full[\s_\-]?name|^name$|^fname$|^lname$/i;
const EMAIL_VALUE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Returns true if the captured fields object carries at least one
// recognisable contact field with a plausible value. This is the
// gate that decides whether a fetch/XHR body is a "real" form
// submission worth shipping to /api/capture.
export function hasRealContactField(fields) {
  if (!fields || typeof fields !== "object") return false;
  for (const key in fields) {
    if (!Object.prototype.hasOwnProperty.call(fields, key)) continue;
    if (key.length > 80) continue;
    if (!CONTACT_KEY_RE.test(key)) continue;
    const raw = fields[key];
    const val = (raw == null ? "" : String(raw)).trim();
    if (val.length === 0) continue;
    // For email-shaped keys, require the value to look like an email.
    // Stops `email_hash` / `email_id` / `hashed_email` (analytics-dim
    // metadata) from being treated as a real submission.
    if (/email/i.test(key) && !EMAIL_VALUE_RE.test(val)) continue;
    return true;
  }
  return false;
}
